import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost, apiPut, apiDelete } from '../../api/client';
import StreamPageVisualEditor from './streamPageEditor/StreamPageVisualEditor';
import { emptyPage, streamPagePublicPath } from './streamPageEditor/shared';
import { DragHandle, SortableItem, reorder } from './pageEditor/sortable';

export default function AdminStreamPages() {
  const [pages, setPages] = useState([]);
  const [form, setForm] = useState(emptyPage());
  const [editingSlug, setEditingSlug] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [showJson, setShowJson] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [previewSchedule, setPreviewSchedule] = useState([]);

  const load = () => apiGet('/admin/stream-pages').then(setPages);

  useEffect(() => {
    load().catch(console.error);
    apiGet('/schedule').then(setPreviewSchedule).catch(console.error);
  }, []);

  const stats = {
    total: pages.length,
    published: pages.filter((p) => p.published).length,
    draft: pages.filter((p) => !p.published).length,
  };

  const startNew = () => {
    setEditingSlug(null);
    setForm(emptyPage());
    setMessage('');
    setEditorOpen(true);
  };

  const startEdit = (page) => {
    setEditingSlug(page.slug);
    setForm({
      slug: page.slug,
      title: page.title,
      navLabel: page.navLabel || page.title,
      showInNav: page.showInNav !== false,
      isSystem: page.isSystem,
      seo: { ...page.seo },
      hero: { ...page.hero },
      content: JSON.parse(JSON.stringify(page.content || { sections: [] })),
      published: page.published,
      sortOrder: page.sortOrder || 0,
    });
    setMessage('');
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingSlug(null);
    setForm(emptyPage());
    setShowJson(false);
    setMessage('');
  };

  const handleSave = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setMessage('');
    try {
      if (editingSlug) {
        await apiPut(`/admin/stream-pages/${editingSlug}`, form);
        setMessage('Stream page saved.');
      } else {
        await apiPost('/admin/stream-pages', form);
        setMessage('Stream page created.');
        setEditingSlug(form.slug);
      }
      load();
    } catch (err) {
      setMessage(err.message);
    }
  };

  const handleDelete = async (slug, isSystem) => {
    if (isSystem) {
      setMessage('System pages cannot be deleted.');
      return;
    }
    if (!window.confirm(`Delete stream page "${slug}"?`)) return;
    await apiDelete(`/admin/stream-pages/${slug}`);
    if (editingSlug === slug) closeEditor();
    load();
  };

  const persistOrder = async (nextPages) => {
    const ordered = nextPages.map((page, index) => ({
      ...page,
      sortOrder: (nextPages.length - index) * 10,
    }));
    setPages(ordered);
    setMessage('');
    try {
      await Promise.all(ordered.map((page) => apiPut(`/admin/stream-pages/${page.slug}`, page)));
      setMessage('Page order saved.');
    } catch (err) {
      setMessage(err.message);
      load();
    }
  };

  const applyJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      setForm(parsed);
      setShowJson(false);
      setMessage('JSON applied to form.');
    } catch (err) {
      setMessage('Invalid JSON: ' + err.message);
    }
  };

  if (editorOpen) {
    return (
      <>
        <StreamPageVisualEditor
          form={form}
          setForm={setForm}
          editingSlug={editingSlug}
          message={message}
          onSave={handleSave}
          onClose={closeEditor}
          onShowJson={() => { setJsonText(JSON.stringify(form, null, 2)); setShowJson(true); }}
          previewSchedule={previewSchedule}
        />
        {showJson && (
          <div className="tpb-json-overlay">
            <div className="admin-json-panel">
              <div className="admin-json-panel-head">
                <h3>JSON editor</h3>
                <button type="button" className="btn btn-secondary" onClick={() => setShowJson(false)}>Close</button>
              </div>
              <textarea className="admin-json" value={jsonText} onChange={(e) => setJsonText(e.target.value)} rows={16} />
              <div className="admin-catalog-form-actions">
                <button type="button" className="btn btn-primary" onClick={applyJson}>Apply JSON</button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="admin-panel-page">
      <div className="admin-panel-toolbar">
        <div className="admin-panel-toolbar-row">
          <div className="admin-stat-chips">
            <div className="admin-stat-chip">
              <span className="admin-stat-value">{stats.total}</span>
              <span className="admin-stat-label">Pages</span>
            </div>
            <div className="admin-stat-chip admin-stat-chip--green">
              <span className="admin-stat-value">{stats.published}</span>
              <span className="admin-stat-label">Published</span>
            </div>
            <div className="admin-stat-chip">
              <span className="admin-stat-value">{stats.draft}</span>
              <span className="admin-stat-label">Draft</span>
            </div>
          </div>
          <div className="admin-panel-toolbar-actions">
            <button type="button" className="btn btn-primary" onClick={startNew}>
              <i className="fas fa-plus" aria-hidden="true" />
              New page
            </button>
          </div>
        </div>
      </div>

      {message && !editorOpen && <p className="admin-message">{message}</p>}

      <div className="admin-list-wrap">
        {pages.length === 0 ? (
          <div className="admin-list-empty">
            <p>No stream pages yet.</p>
            <button type="button" className="btn btn-primary" onClick={startNew}>Create first page</button>
          </div>
        ) : (
          <ul className="admin-list">
            {pages.map((p, index) => (
              <SortableItem
                as="li"
                key={p.slug}
                listId="stream-page-order"
                index={index}
                className="admin-list-item"
                onMove={(from, to) => persistOrder(reorder(pages, from, to))}
              >
                <DragHandle listId="stream-page-order" index={index} />
                {p.hero?.image ? (
                  <img src={p.hero.image} alt="" className="admin-thumb" />
                ) : (
                  <span className="admin-thumb is-empty"><i className="fas fa-broadcast-tower" aria-hidden="true" /></span>
                )}
                <div className="admin-list-main">
                  <div className="admin-list-title-row">
                    <span className="admin-list-title">{p.title}</span>
                    <span className="admin-list-sub">{streamPagePublicPath(p.slug)}</span>
                  </div>
                  <div className="admin-list-pills">
                    <span className={`admin-pill ${p.published ? 'is-green' : 'is-muted'}`}>
                      {p.published ? 'Published' : 'Draft'}
                    </span>
                    {p.isSystem && <span className="admin-pill is-muted">System</span>}
                    {p.showInNav && <span className="admin-pill is-muted">In nav</span>}
                    <span className="admin-pill is-muted">{p.content?.sections?.length || 0} sections</span>
                  </div>
                </div>
                <div className="admin-list-actions">
                  {p.published && (
                    <Link to={streamPagePublicPath(p.slug)} target="_blank" rel="noopener noreferrer" className="admin-icon-btn" title="View">
                      <i className="fas fa-external-link-alt" aria-hidden="true" />
                    </Link>
                  )}
                  <button type="button" className="admin-icon-btn" onClick={() => startEdit(p)} title="Edit">
                    <i className="fas fa-pen" aria-hidden="true" />
                  </button>
                  {!p.isSystem && (
                    <button type="button" className="admin-icon-btn danger" onClick={() => handleDelete(p.slug, p.isSystem)} title="Delete">
                      <i className="fas fa-trash" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </SortableItem>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
